import { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, MapPin, Clock, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { SEO } from '@/components/SEO';

export function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    
    try {
      const res = await fetch('/kontakt.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData).toString(),
      });
      
      if (res.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const faqs = [
    { q: 'Wie lange dauert die Antwort?', a: 'In der Regel antworten wir innerhalb von 24–48 Stunden. An Wochenenden und Feiertagen kann es etwas länger dauern.' },
    { q: 'Kann ich JL-Software unterstützen?', a: 'Der beste Support ist eine ehrliche Bewertung oder eine Weiterempfehlung. Für Spenden oder Kooperationen schreib uns einfach.' },
    { q: 'Ich habe einen Bug gefunden – was nun?', a: 'Beschreibe das Problem so genau wie möglich (Gerät, Browser, Schritte zur Reproduktion). Screenshots helfen enorm.' },
    { q: 'Gibt es eine Telefon-Hotline?', a: 'Nein. Als Einzelunternehmen bieten wir ausschließlich E-Mail-Support an. Das garantiert eine schriftliche, nachvollziehbare Kommunikation.' },
  ];

  return (
    <>
      <SEO title="Kontakt" description="Kontaktiere JL-Software – wir freuen uns auf deine Nachricht." path="/kontakt" />
      <div className="mt-[70px]">
        {/* Hero */}
        <section className="relative pt-20 pb-12 text-center border-b border-white/[0.06] overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full bg-gold/[0.05] blur-3xl pointer-events-none" />
          <div className="relative z-10 max-w-2xl mx-auto px-6">
            <h1 className="font-display text-4xl md:text-5xl font-bold italic text-glow mb-4">
              Kontakt<span className="text-gold">aufnehmen</span>
            </h1>
            <p className="text-text-secondary font-light leading-relaxed max-w-lg mx-auto">
              Hast du Fragen, Feedback oder eine Idee? Wir freuen uns auf deine Nachricht.
              Antworten kommen in der Regel innerhalb von 24–48 Stunden.
            </p>
          </div>
        </section>

        <section className="max-w-[1100px] mx-auto px-6 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Info */}
            <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="lg:col-span-2 space-y-5">
              <div className="bg-surface rounded-2xl border border-white/[0.06] p-6 hover:border-gold/25 transition-all">
                <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.1em] text-text-muted mb-3">
                  <Mail size={14} /> E-Mail
                </h3>
                <a href="mailto:info@jls-software.de" className="text-gold hover:underline text-lg">
                  Info@jls-software.de
                </a>
                <p className="text-text-muted text-sm mt-2">Dies ist der schnellste Weg, uns zu erreichen.</p>
              </div>

              <div className="bg-surface rounded-2xl border border-white/[0.06] p-6 hover:border-gold/25 transition-all">
                <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.1em] text-text-muted mb-3">
                  <MapPin size={14} /> Postanschrift
                </h3>
                <p className="text-text-primary font-semibold text-lg">Joachim Leiskau</p>
                <p className="text-text-secondary mt-1">Alkesis-Kivi-Str. 2<br />18016 Rostock<br />Deutschland</p>
              </div>

              <div className="bg-surface rounded-2xl border border-white/[0.06] p-6 hover:border-gold/25 transition-all">
                <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.1em] text-text-muted mb-3">
                  <Clock size={14} /> Erreichbarkeit
                </h3>
                <p className="text-text-secondary text-sm">
                  Da es sich um ein Einzelunternehmen handelt, bitten wir um etwas Geduld. Wir geben uns Mühe, jede Anfrage persönlich und zeitnah zu beantworten.
                </p>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="lg:col-span-3">
              <div className="bg-surface rounded-2xl border border-white/[0.06] p-8 gradient-border">
                {status === 'success' ? (
                  <div className="text-center py-8">
                    <CheckCircle size={56} className="text-gold mx-auto mb-4" />
                    <h2 className="text-2xl font-bold text-text-primary mb-2">Nachricht gesendet!</h2>
                    <p className="text-text-secondary mb-6">Wir melden uns so schnell wie möglich bei dir.</p>
                    <button onClick={() => setStatus('idle')} className="px-6 py-2.5 bg-gold-dim text-gold border border-gold/20 rounded-xl text-sm font-semibold hover:bg-gold/25 transition-all">
                      Neue Nachricht
                    </button>
                  </div>
                ) : (
                  <>
                    <h2 className="text-xl font-bold text-text-primary mb-1">Schreib uns direkt</h2>
                    <p className="text-text-secondary text-sm mb-6">Fülle das Formular aus – wir antworten per E-Mail.</p>

                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-1.5">Name</label>
                          <input
                            required
                            value={formData.name}
                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                            className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary placeholder:text-text-muted focus:border-gold focus:bg-gold/5 focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all"
                            placeholder="Dein Name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-text-secondary mb-1.5">E-Mail</label>
                          <input
                            required
                            type="email"
                            value={formData.email}
                            onChange={e => setFormData({ ...formData, email: e.target.value })}
                            className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary placeholder:text-text-muted focus:border-gold focus:bg-gold/5 focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all"
                            placeholder="deine@email.de"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1.5">Betreff</label>
                        <input
                          required
                          value={formData.subject}
                          onChange={e => setFormData({ ...formData, subject: e.target.value })}
                          className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary placeholder:text-text-muted focus:border-gold focus:bg-gold/5 focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all"
                          placeholder="Worum geht es?"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-secondary mb-1.5">Nachricht</label>
                        <textarea
                          required
                          value={formData.message}
                          onChange={e => setFormData({ ...formData, message: e.target.value })}
                          rows={5}
                          className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.06] rounded-xl text-text-primary placeholder:text-text-muted focus:border-gold focus:bg-gold/5 focus:outline-none focus:ring-2 focus:ring-gold/10 transition-all resize-y"
                          placeholder="Deine Nachricht..."
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={status === 'sending'}
                        className="w-full flex items-center justify-center gap-2 py-3.5 bg-gold text-surface-primary font-bold rounded-xl shadow-gold hover:shadow-gold-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                      >
                        <Send size={16} />{status === 'sending' ? 'Wird gesendet...' : 'Nachricht senden'}
                      </button>
                      {status === 'error' && (
                        <div className="flex items-center gap-2 text-crimson text-sm bg-crimson-dim border border-crimson/20 rounded-xl px-4 py-3">
                          <AlertCircle size={16} /> Fehler beim Senden. Bitte versuche es später erneut.
                        </div>
                      )}
                    </form>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </section>

        {/* FAQ */}
        <section className="border-y border-white/[0.06] bg-surface-secondary/50 py-16">
          <div className="max-w-[800px] mx-auto px-6">
            <div className="text-center mb-10">
              <span className="inline-block text-crimson text-xs font-bold uppercase tracking-[0.15em] mb-3">
                Häufige Fragen
              </span>
              <h2 className="text-2xl md:text-3xl font-extrabold text-text-primary">
                Bevor du schreibst
              </h2>
            </div>

            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-surface rounded-2xl border border-white/[0.06] p-5 hover:border-gold/25 transition-all"
                >
                  <h3 className="flex items-center gap-2 text-base font-bold text-text-primary mb-2">
                    <span className="w-7 h-7 flex items-center justify-center bg-gold-dim text-gold rounded-lg text-xs font-extrabold flex-shrink-0">?</span>
                    {faq.q}
                  </h3>
                  <p className="text-text-secondary text-sm leading-relaxed pl-9">
                    {faq.a}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}